import { useId } from 'react'

import { PlanSubmissionDownloadFormat } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  MenuRoot,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaChevronDownRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { iconSize } from '~/constants/iconSize'

/**
 * chakra v3 の Menu slot の型は本プロジェクトの型解決で `children` / `value` / `onSelect` /
 * style props が欠落するため、 本来の prop シグネチャへ cast して復元する
 * (MasterTableActionBar と同型の cast パターン)。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & { value: string; onSelect?: () => void }
>

type Props = {
  /** 選んだ形式でダウンロードを実行する。 */
  onDownload: (format: PlanSubmissionDownloadFormat) => void
  /** true の時はメニューを開かせない (AC-PPL-027: 活性可否は XML / ZIP 共通)。 */
  disabled: boolean
  /** 非活性の時にツールチップで示す押せない理由 (AC-PPL-027)。 無ければツールチップを付けない。 */
  disabledReason?: string
}

/**
 * 需要調達計画一覧の行「ダウンロード」ボタン。 押すと XML / ZIP の形式選択メニューが開く
 * (AC-PLC-006)。 見た目は Des 判断 (issue #9870、 2026-10-08) による:
 *
 * - ボタンは先頭のダウンロードアイコン + 文字「ダウンロード」+ DS の chevron アイコン。
 *   先頭のアイコンは全画面共通の決まり (UI 辞書 components.md「CSV 導線の文言・アイコン規約」、
 *   2026-10-27 デザイナー決定で #9870 の「先頭のアイコンは付けない」を廃止)。
 * - メニューの各項目は形式名 + 用途 1 行の 2 行項目 (選ぶ理由が画面から分かるように)。
 *
 * DS `Menu` の `content[].label` は string 固定で 2 行項目を作れないため、 DS の Menu slot を
 * 直接組む (MasterTableActionBar の 2 行項目と同じ方式)。
 *
 * 共有の `XmlDownloadButton` (XML 単独) は発電販売計画一覧が使い続けるため触らず、
 * 形式選択が要る需要調達計画一覧だけこの component を使う。
 *
 * 非活性の時は Menu で包まずボタンだけを描画する。 ecloud の `TertiaryButton` の disabled は
 * native `disabled` ではなく aria-disabled なので、 Menu の trigger にすると非活性でも開いてしまう
 * 可能性がある (DemandForecastDaily/Toolbar の知見)。 実ブラウザで非活性行のメニューが開かない
 * ことは F6 (procurement-plan-download.spec.ts) が固定する。
 */
export const DownloadFormatMenuButton = ({
  onDownload,
  disabled,
  disabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const button = (
    // ラベルが可視なので hover の説明は付けない (XmlDownloadButton と同じ)。
    // 表のセル内のボタンは全て DS の M サイズ (`size="md"` = 高さ 40px。 デザイナー判断 2026-10-30)。
    // 隣の「詳細」ボタン (ProcurementPlanTable) と同じく recipe の既定のまま揃える。
    // メニューが開いている間は chevron を上向きにする (Des の見本 issue #9870)。 Menu の trigger は
    // 開閉を `data-state` で持つので、 アイコンを差し替えずに向きだけ反転する。 回すのは右端の
    // chevron だけで、 先頭のダウンロードアイコンは回さない。
    <TertiaryButton
      _open={{ '& [data-menu-caret]': { transform: 'rotate(180deg)' } }}
      disabled={disabled}
      rightIcon={<FaChevronDownRegular data-menu-caret="" />}
      size="md"
    >
      <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
      {t('page:procurementPlanList.downloadXml.menuButtonLabel')}
    </TertiaryButton>
  )

  if (disabled) {
    return <ButtonTooltip content={disabledReason}>{button}</ButtonTooltip>
  }

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }}>
      <MenuTrigger asChild>{button}</MenuTrigger>
      <MenuPositioner>
        <MenuContent>
          <FormatMenuItem
            description={t(
              'page:procurementPlanList.downloadXml.formatXmlDescription',
            )}
            onSelect={() => {
              onDownload(PlanSubmissionDownloadFormat.XML)
            }}
            title={t('page:procurementPlanList.downloadXml.formatXml')}
            value="xml"
          />
          <FormatMenuItem
            description={t(
              'page:procurementPlanList.downloadXml.formatZipDescription',
            )}
            onSelect={() => {
              onDownload(PlanSubmissionDownloadFormat.ZIP)
            }}
            title={t('page:procurementPlanList.downloadXml.formatZip')}
            value="zip"
          />
        </MenuContent>
      </MenuPositioner>
    </MenuRoot>
  )
}

type FormatMenuItemProps = {
  title: string
  description: string
  value: string
  onSelect: () => void
}

/**
 * 形式名 + 用途 1 行の 2 行項目。 accessible name は形式名だけにし、 用途は
 * `aria-describedby` で補足として読ませる (RichActionMenu と同じ方針: 名前まで長くすると
 * 選択肢の聞き分けが悪くなる)。
 */
const FormatMenuItem = ({
  title,
  description,
  value,
  onSelect,
}: FormatMenuItemProps): React.ReactElement => {
  const descriptionId = useId()
  return (
    <MenuItem
      alignItems="flex-start"
      flexDirection="column"
      gap={0.5}
      whiteSpace="normal"
      aria-describedby={descriptionId}
      aria-label={title}
      onSelect={onSelect}
      paddingY={2}
      value={value}
    >
      <Text fontWeight="semibold">{title}</Text>
      <Box id={descriptionId}>
        <Text textStyle="menuItemDesc" color="Text.midEmphasis">
          {description}
        </Text>
      </Box>
    </MenuItem>
  )
}
